import api from './api'

export interface VendorDetailItem {
  id: string
  display_name: string
  company_name: string
  print_on_check_name: string
  active: boolean
  balance: number
  gross_spend_ltm: number
  expense_txn_count_ltm: number
  tax_identifier: string
  acct_num: string
  vendor_1099: boolean
  payment_method: string
  overseen_by: string
  liability_timing: string
  bill_addr_line1: string
  bill_addr_line2: string
  bill_addr_line3: string
  bill_addr_city: string
  bill_addr_state: string
  bill_addr_postal_code: string
  bill_addr_country: string
  primary_phone: string
  mobile: string
  fax: string
  primary_email: string
  web_url: string
  notes: string
  is_faire_vendor: boolean
  term_ref: string
  url: string
}

export interface VendorDetailsResponse {
  vendors: VendorDetailItem[]
  total: number
  ltm_start_date: string
  ltm_end_date: string
  ltm_stats_synced_at?: string | null
}

export interface VendorExportDataUpdate {
  payment_method?: string | null
  overseen_by?: string | null
  liability_timing?: string | null
}

export interface VendorLtmStatsRefreshResponse {
  success: boolean
  vendors_with_stats: number
  ltm_start_date: string
  ltm_end_date: string
  synced_at: string
}

export interface VendorExportCsvImportResponse {
  success: boolean
  rows_processed: number
  rows_upserted: number
  rows_skipped: number
  unmatched_vendors: string[]
}

export async function getVendorDetails(refresh = false): Promise<VendorDetailsResponse> {
  const response = await api.get<VendorDetailsResponse>('/vendor-details', {
    params: { refresh },
  })
  return response.data
}

export async function refreshVendorLtmStats(): Promise<VendorLtmStatsRefreshResponse> {
  const response = await api.post<VendorLtmStatsRefreshResponse>(
    '/vendor-details/refresh-stats'
  )
  return response.data
}

export async function updateVendorExportData(
  qboVendorId: string,
  data: VendorExportDataUpdate
): Promise<void> {
  await api.put(`/vendor-details/${qboVendorId}`, data)
}

export async function importVendorExportCsv(file: File): Promise<VendorExportCsvImportResponse> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<VendorExportCsvImportResponse>(
    '/vendor-details/import-csv',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

export type SpendAnalyticsGranularity = 'month' | 'quarter' | 'year'
export type SpendAnalyticsBasis = 'gross' | 'net'
export type SpendAnalyticsGroupBy = 'category' | 'location'

export interface SpendAnalyticsTotals {
  gross: number
  net: number
  expense_txn_count: number
  bill_count: number
  vendor_credit_count: number
}

export interface PeriodSegmentAmount {
  segment: string
  amount: number
}

export interface SpendAnalyticsPeriod {
  period: string
  gross: number
  net: number
  by_segment: PeriodSegmentAmount[]
}

export interface SegmentBreakdownItem {
  segment: string
  gross: number
  net: number
  pct: number
}

export interface VendorSpendAnalyticsResponse {
  vendor_id: string
  display_name: string
  start_date: string
  end_date: string
  granularity: SpendAnalyticsGranularity
  basis: SpendAnalyticsBasis
  group_by: SpendAnalyticsGroupBy
  totals: SpendAnalyticsTotals
  periods: SpendAnalyticsPeriod[]
  segment_breakdown: SegmentBreakdownItem[]
}

export interface VendorSpendAnalyticsParams {
  startDate: string
  endDate: string
  granularity?: SpendAnalyticsGranularity
  basis?: SpendAnalyticsBasis
  groupBy?: SpendAnalyticsGroupBy
}

export interface SpendTransactionLine {
  amount: number
  category: string
  location: string
}

export interface SpendTransaction {
  type: 'bill' | 'purchase' | 'vendor_credit'
  id: string
  txn_date: string
  total_amt: number
  lines: SpendTransactionLine[]
}

export interface VendorSpendTransactionsResponse {
  vendor_id: string
  display_name: string
  start_date: string
  end_date: string
  transactions: SpendTransaction[]
}

export interface VendorSpendTransactionsParams {
  startDate: string
  endDate: string
}

export async function getVendorSpendTransactions(
  qboVendorId: string,
  params: VendorSpendTransactionsParams
): Promise<VendorSpendTransactionsResponse> {
  const response = await api.get<VendorSpendTransactionsResponse>(
    `/vendor-details/${qboVendorId}/spend-transactions`,
    {
      params: {
        start_date: params.startDate,
        end_date: params.endDate,
      },
    }
  )
  return response.data
}

export async function getVendorSpendAnalytics(
  qboVendorId: string,
  params: VendorSpendAnalyticsParams
): Promise<VendorSpendAnalyticsResponse> {
  const response = await api.get<VendorSpendAnalyticsResponse>(
    `/vendor-details/${qboVendorId}/spend-analytics`,
    {
      params: {
        start_date: params.startDate,
        end_date: params.endDate,
        granularity: params.granularity ?? 'month',
        basis: params.basis ?? 'net',
        group_by: params.groupBy ?? 'category',
      },
    }
  )
  return response.data
}
